<template>
  <b-card no-body>
    <!-- ? Command Name and Description Start -->
    <div class="card__header card-padding-top card-padding-x">
      <span class="header__label">Backup Command Template</span>
      <div class="custom-max-width">
        <atom-input
          v-model="targetTemplateDetail.autoBackupConfigName"
          title="Backup Command Template Name"
          placeholder="Template Name"
          class="mt-2"
          errorMessage="Template name is required"
          :isAsterisk="true"
          :isError="isErrorAtTemplateName"
          :isDisable="!hasUpdatePermission"
        ></atom-input>
        <atom-text-area
          v-model="targetTemplateDetail.autoBackupConfigDescription"
          title="Description"
          placeholder="Description"
          :isDisable="!hasUpdatePermission"
          class="mt-1"
        ></atom-text-area>
        <div
          name="checkbox-and-tooltip__wrapper"
          class="d-flex align-items-center"
        >
          <atom-checkbox
            v-model="targetTemplateDetail.isForCiscoDevice"
            idx="cisco-device"
            title="Cisco Device"
            class="mt-1"
            :isColorTextOnActive="true"
            :isDisable="!hasUpdatePermission"
          ></atom-checkbox>
          <div id="info-tooltip" class="info-icon_container mt-1 ml-1">
            <feather-icon icon="InfoIcon" stroke="#6E6B7B" size="12" />
          </div>
          <b-tooltip
            target="info-tooltip"
            placement="right"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            >Select when the backup configuration is from Cisco
            devices.</b-tooltip
          >
        </div>
      </div>
    </div>
    <!-- ? Command Name and Description End -->
    <hr />
    <!-- ?  Backup Command List Start -->
    <div class="card__body card-padding">
      <span class="header__label">Backup Command List</span>
      <!-- ? Command Section Wrapper Overflow X Scroll Start -->
      <div class="command__container">
        <div class="command-section__wrapper">
          <div
            v-for="(backupCommand, index) in targetTemplateDetail.commands"
            :key="index"
            class="row mt-1"
          >
            <div class="col-3">
              <atom-input
                v-model="backupCommand.command"
                placeholder="Command"
                errorMessage="Required"
                :title="index === 0 ? 'Command' : ''"
                :isAsterisk="true"
                :isError="
                  isValidateBackupCommandListTrigger ||
                  isValidateAutoConfigTemplateDetailTrigger
                    ? isEmptyString(backupCommand.command)
                    : false
                "
                :isDisable="!hasUpdatePermission"
              ></atom-input>
            </div>
            <div class="col-3">
              <atom-input
                v-model="backupCommand.prompt"
                placeholder="Prompt"
                errorMessage="Required"
                :title="index === 0 ? 'Prompt' : ''"
                :isAsterisk="true"
                :isError="
                  isValidateBackupCommandListTrigger ||
                  isValidateAutoConfigTemplateDetailTrigger
                    ? isEmptyString(backupCommand.prompt)
                    : false
                "
                :isDisable="!hasUpdatePermission"
              ></atom-input>
            </div>
            <div class="col-2">
              <atom-input
                v-model="backupCommand.timeout"
                placeholder="Timeout (ms)"
                errorMessage="Number Required"
                :title="index === 0 ? 'Timeout (ms)' : ''"
                :isAsterisk="true"
                :isError="
                  isValidateBackupCommandListTrigger ||
                  isValidateAutoConfigTemplateDetailTrigger
                    ? isNotNumeric(backupCommand.timeout)
                    : false
                "
                :isDisable="!hasUpdatePermission"
              ></atom-input>
            </div>
            <div class="col-2">
              <div
                v-if="index === 0"
                name="title-and-tooltip__wrapper"
                class="d-flex justify-content-center align-items-center"
              >
                <span class="text-input--title text-nowrap"
                  >Backup Response</span
                >
                <div
                  id="backup-response-tooltip"
                  class="info-icon_container ml-1"
                >
                  <feather-icon icon="InfoIcon" stroke="#6E6B7B" size="12" />
                </div>
                <b-tooltip
                  boundary="window"
                  placement="top"
                  target="backup-response-tooltip"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  >When the backup response is specified as "Yes", the response
                  of the respective command is read and downloaded from the
                  device as config file data</b-tooltip
                >
              </div>
              <div
                name="switch__wrapper"
                class="d-flex justify-content-center align-items-center custom-margin-top"
              >
                <atom-switch
                  v-model="backupCommand.backupResponse"
                  :isDisable="!hasUpdatePermission"
                ></atom-switch>
              </div>
            </div>
            <div v-if="hasUpdatePermission" :class="['col-2 d-flex', { 'mt-2': index === 0 }]">
              <atom-button
                buttonId="add-new-command"
                iconLeft="PlusIcon"
                :isOnlyIcon="true"
                @click="onClickAddNewCommand(index)"
              ></atom-button>
              <atom-button
                v-if="index !== 0"
                buttonId="delete-command"
                buttonStyle="border"
                buttonColor="secondary"
                iconLeft="Trash2Icon"
                class="ml-2"
                :isOnlyIcon="true"
                @click="onClickDeleteCommand(index)"
              ></atom-button>
            </div>
          </div>
          <!-- ?  Backup Command List End -->
        </div>
      </div>
      <!-- ? Command Section Wrapper Overflow X Scroll End -->
    </div>
    <div
      v-if="hasUpdatePermission"
      name="button-section"
      class="card-padding mt-2 d-flex justify-content-between"
    >
      <div class="button-group-left delete--button">
        <atom-button
          v-if="isShowDeleteButton"
          buttonId="delete-auto-config-button"
          :styleInButton="deleteButtonStyle"
          @click="onClickDeleteAutoBackupConfigButton"
          ><feather-icon
            icon="Trash2Icon"
            class="delete__icon--style"
          />Delete</atom-button
        >
      </div>
      <div class="button-group-right d-flex justify-content-end">
        <atom-button
          v-if="isShowSaveChangesButton"
          buttonId="save-changes-auto-config-button"
          :isDisable="isDisableSaveChangesButton"
          @click="onClickSaveChangesAutoBackupConfigButton"
        >
          Save Changes
        </atom-button>

        <atom-button
          v-else
          class="custom-primary-button-width"
          buttonId="create-auto-config-button"
          :isDisable="isDisableCreateButton"
          @click="onClickCreateNewAutoBackupConfigButton"
        >
          Create
        </atom-button>

        <atom-button
          class="ml-1"
          buttonId="cancel-create-edit-auto-config-button"
          buttonStyle="border"
          buttonColor="secondary"
          @click="onClickCancelCreateEditAutoBackupConfigCommandButton"
        >
          Cancel
        </atom-button>
      </div>
    </div>
    <!-- ? Modal Section Start -->
    <!-- * Modal Confirm Create New Auto Backup Config Start-->
    <molecule-modal-dialog
      modalId="modal-create-new-auto-backup-config"
      buttonTextLeft="Create"
      :isTextCenter="true"
      :isCloseOnBackDrop="false"
      :textTitle="`Do you want to create auto backup\n“${targetTemplateDetail.autoBackupConfigName}” ?`"
      :textSubtitle="`This Auto Backup Config will be add to the List`"
      @clickButtonLeft="onClickConfirmCreateNewAutoBackupConfigFromModal"
      @clickButtonRight="onClickCancelCreateNewAutoBackupConfigFromModal"
      @clickIconClose="onClickCancelCreateNewAutoBackupConfigFromModal"
    ></molecule-modal-dialog>
    <!-- * Modal Confirm Create New Auto Backup Config End-->

    <!-- * Modal Confirm Save Change Auto Backup Config Start-->
    <molecule-modal-dialog
      modalId="modal-confirm-save-change-auto-backup-config"
      buttonTextLeft="Save Changes"
      :isTextCenter="true"
      :textTitle="`Do you want to save changes\n“${targetTemplateDetail.autoBackupConfigName}” ?`"
      @clickButtonLeft="onClickConfirmSaveChangesAutoBackupConfigFromModal"
    ></molecule-modal-dialog>
    <!-- * Modal Confirm Save Change Auto Backup Config End-->

    <!-- * Modal Confirm Delete Auto Backup Config End-->
    <molecule-modal-dialog
      modalId="modal-confirm-delete-auto-backup-config"
      icon="AlertCircleIcon"
      :isTextCenter="true"
      :textTitle="`Do you want to delete\n${targetTemplateDetail.autoBackupConfigName}?`"
      textSubtitle="You won’t be able to revert this!"
      buttonTextLeft="Delete"
      buttonTextRight="Cancel"
      buttonColorLeft="danger"
      @clickButtonLeft="onClickConfirmDeleteAutoBackupConfigFromModal"
    ></molecule-modal-dialog>
    <!-- * Modal Confirm Delete Auto Backup Config End-->

    <!-- * Modal Discard Change Start-->
    <molecule-modal-dialog
      modalId="modal-confirm-discard-change"
      :isTextCenter="true"
      textTitle="Do you want to leave without finishing?"
      buttonTextLeft="Confirm"
      @clickButtonLeft="onClickConfirmDiscardChangeFromModal"
    >
    </molecule-modal-dialog>
    <!-- * Modal Discard Change End-->
    <!-- ? Modal Section End -->
  </b-card>
</template>

<script>
const replacer = (key, value) =>
  value instanceof Object && !(value instanceof Array)
    ? Object.keys(value)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = value[key]
          return sorted
        }, {})
    : value

import { BCard, VBTooltip, BTooltip } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomCheckbox from "@/components/atoms/AtomCheckbox.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSwitch from "@/components/atoms/AtomSwitch.vue"
import AtomTextArea from "@/components/atoms/AtomTextArea.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"

import Ripple from "vue-ripple-directive"
import mixinsValidator from "@/mixins/mixinsValidator"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismCreateEditCommands",
  props: {
    originAutoBackupConfig: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  components: {
    AtomButton,
    AtomCheckbox,
    AtomInput,
    AtomSwitch,
    AtomTextArea,
    BCard,
    BTooltip,
    MoleculeModalDialog,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  mixins: [mixinsValidator, mixinsUser],
  data() {
    return {
      deleteButtonStyle:
        "background-color: #F6E4E4 !important;color: #EA5455 !important; border-color: #F6E4E4 !important",
      modalId: null,
      isValidateAutoConfigTemplateDetailTrigger: false,
      isValidateBackupCommandListTrigger: false,
      isBackupCommandDetailChange: false,
      targetTemplateDetail: {
        autoBackupConfigId: "",
        autoBackupConfigName: "",
        autoBackupConfigDescription: "",
        isForCiscoDevice: false,
        commands: [],
      },
    }
  },
  computed: {
    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.NCM.BACKUP_SETTINGS_WRITE)
    },

    // BUTTON LOGIC START
    isShowSaveChangesButton() {
      return this.hasString(this.autoBackupConfigId)
    },
    isShowDeleteButton() {
      return this.hasString(this.autoBackupConfigId)
    },
    isDisableSaveChangesButton() {
      return (
        !this.isAutoBackupConfigDetailChange ||
        this.isErrorAtTemplateName ||
        this.isErrorOccurAtCurrentBackupCommandList
      )
    },
    isDisableCreateButton() {
      return this.isValidateAutoConfigTemplateDetailTrigger
        ? this.isErrorOccurAtCreateNewTemplate
        : false
    },
    // BUTTON LOGIC END
    autoBackupConfigId() {
      return this.$route?.params?.id
    },
    isErrorAtTemplateName() {
      return this.isValidateAutoConfigTemplateDetailTrigger
        ? this.isEmptyString(this.targetTemplateDetail.autoBackupConfigName)
        : false
    },
    isErrorOccurAtCurrentBackupCommandList() {
      return this.isValidateBackupCommandListTrigger ||
        this.isValidateAutoConfigTemplateDetailTrigger
        ? this.targetTemplateDetail.commands.some((backup) => {
            return (
              this.isEmptyString(backup.command) ||
              this.isEmptyString(backup.prompt) ||
              this.isNotNumeric(backup.timeout)
            )
          })
        : false
    },
    isAutoBackupConfigDetailChange() {
      let originAutoBackupConfigDetail = JSON.stringify(
        this.originAutoBackupConfig,
        replacer
      )
      let currentAutoBackupConfigDetail = JSON.stringify(
        this.targetTemplateDetail,
        replacer
      )
      return originAutoBackupConfigDetail !== currentAutoBackupConfigDetail
    },
  },
  mounted() {
    const baseBackupCommand = {
      command: "",
      prompt: "",
      timeout: "",
      backupResponse: false,
    }
    this.targetTemplateDetail.commands.push(baseBackupCommand)
  },

  watch: {
    originAutoBackupConfig: {
      immediate: true,
      deep: true,
      handler() {
        if (this.originAutoBackupConfig) {
          this.targetTemplateDetail = JSON.parse(
            JSON.stringify(this.originAutoBackupConfig)
          )
        }
      },
    },
  },
  methods: {
    setTargetBackupCommandToDefault() {
      this.targetBackupCommand = {
        command: "",
        prompt: "",
        timeout: "",
        backupResponse: false,
      }
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = ""
    },

    navigateTo(path) {
      this.$router.push(path)
    },

    onClickAddNewCommand(targetIndex) {
      this.isValidateBackupCommandListTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccurAtCurrentBackupCommandList) {
          const newBackupCommand = {
            command: "",
            prompt: "",
            timeout: "",
            backupResponse: false,
          }
          this.targetTemplateDetail.commands.splice(
            targetIndex + 1,
            0,
            newBackupCommand
          )
          this.isValidateBackupCommandListTrigger = false
        }
      })
    },

    onClickDeleteCommand(targetIndex) {
      this.targetTemplateDetail.commands.splice(targetIndex, 1)
    },

    onClickCreateNewAutoBackupConfigButton() {
      this.isValidateAutoConfigTemplateDetailTrigger = true
      this.$nextTick(() => {
        if (
          !this.isErrorAtTemplateName &&
          !this.isErrorOccurAtCurrentBackupCommandList
        ) {
          this.modalId = "modal-create-new-auto-backup-config"
          this.$bvModal.show(this.modalId)
        }
      })
    },

    onClickConfirmCreateNewAutoBackupConfigFromModal() {
      const newAutoBackupConfigData = JSON.parse(
        JSON.stringify(this.targetTemplateDetail)
      )
      this.$emit("onRequestCreateNewAutoBackupConfig", newAutoBackupConfigData)
      this.closeModal()
    },

    onClickCancelCreateNewAutoBackupConfigFromModal() {
      this.isValidateAutoConfigTemplateDetailTrigger = false
    },

    onClickSaveChangesAutoBackupConfigButton() {
      this.isValidateAutoConfigTemplateDetailTrigger = true
      this.$nextTick(() => {
        if (
          !this.isErrorAtTemplateName &&
          !this.isErrorOccurAtCurrentBackupCommandList
        ) {
          this.modalId = "modal-confirm-save-change-auto-backup-config"
          this.$bvModal.show(this.modalId)
        }
      })
    },

    onClickConfirmSaveChangesAutoBackupConfigFromModal() {
      const autoBackupConfigId = this.autoBackupConfigId
      const editedAutoBackupConfigData = JSON.parse(
        JSON.stringify(this.targetTemplateDetail)
      )
      this.$emit(
        "onRequestSaveChangesAutoBackupConfig",
        autoBackupConfigId,
        editedAutoBackupConfigData
      )
      this.closeModal()
    },

    onClickCancelCreateEditAutoBackupConfigCommandButton() {
      this.modalId = "modal-confirm-discard-change"
      this.$bvModal.show(this.modalId)
    },

    onClickConfirmDiscardChangeFromModal() {
      this.navigateTo("/network-configuration/backup-settings")
    },

    onClickDeleteAutoBackupConfigButton() {
      this.modalId = "modal-confirm-delete-auto-backup-config"
      this.$bvModal.show(this.modalId)
    },

    onClickConfirmDeleteAutoBackupConfigFromModal(){
      const autoBackupConfigId = this.autoBackupConfigId
      this.$emit("onRequestDeleteAutoBackupConfig", autoBackupConfigId)
      this.closeModal()
      this.navigateTo("/network-configuration/backup-settings")
    }
  },
}
</script>

<style lang="scss" scoped>
hr {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 0;
  border-top: 1px solid #ebe9f1;
}

.delete--button ::v-deep {
  .btn-primary:hover:not(.disabled):not(:disabled) {
    box-shadow: none;
  }
}

.delete__icon--style {
  margin-right: 5px;
}

.card-padding {
  padding: 21px;
}

.card-padding-x {
  padding-left: 21px;
  padding-right: 21px;
}

.card-padding-y {
  padding-top: 21px;
  padding-bottom: 21px;
}

.card-padding-top {
  padding-top: 21px;
}

.card-padding-bottom {
  padding-bottom: 21px;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.text-input--title {
  color: #5e5873;
  font-size: 12px;
}

.custom-primary-button-width {
  width: 125px;
}

.custom-margin-top {
  margin-top: 9px;
}

.command__container {
  overflow: auto;
  padding: 10px 0 10px 0;
}

.command-section__wrapper {
  width: 950px;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .custom-max-width {
    max-width: 75%;
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}
</style>
